import { useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Download, Loader2 } from "lucide-react";

import { Button } from "@app/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { Switch } from "@app/components/ui/switch";
import { cn } from "@app/lib/utils";
import type { SurvivalAnalysisRequest, SurvivalEvent, SurvivalResult } from "@app/types/study";

import { formatInterval, formatNumber, formatP, formatShare } from "./format";
import { groupColor } from "@app/lib/palette";
import { MethodsBox, WarningsList } from "./StudyNotes";
import { SurvivalChart } from "./SurvivalChart";

interface Props {
  settings: SurvivalAnalysisRequest;
  onSettings: (next: SurvivalAnalysisRequest) => void;
  result: SurvivalResult | null;
  loading: boolean;
  error: string | null;
  onExportCsv: () => void;
  exporting: boolean;
}

/** « Survie sans reprise » and « délai jusqu'à l'intervention »: Kaplan-Meier, log-rank. */
export function SurvivalAnalysis({ settings, onSettings, result, loading, error, onExportCsv, exporting }: Props) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const set = (patch: Partial<SurvivalAnalysisRequest>) => onSettings({ ...settings, ...patch });
  const revision = settings.event === "revision";
  // ``null`` until the reader chooses: bands on for two groups, off past that.
  const [bands, setBands] = useState<boolean | null>(null);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="space-y-1">
          <span className="block text-xs font-medium text-slate-500">
            <Trans>Événement étudié</Trans>
          </span>
          <Select
            value={settings.event}
            onValueChange={(event) => set({ event: event as SurvivalEvent, horizons: [] })}
          >
            <SelectTrigger className="w-80">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="revision">{t`Reprise après l'intervention (survie sans reprise)`}</SelectItem>
              <SelectItem value="intervention">{t`Intervention après l'ouverture de l'épisode`}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {revision && (
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
            <Switch
              checked={settings.include_revisions}
              onCheckedChange={(include_revisions) => set({ include_revisions })}
            />
            <Trans>Inclure les reprises elles-mêmes</Trans>
          </label>
        )}
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={onExportCsv}
            disabled={!result || exporting}
            className="gap-1.5 hover:border-primary/50"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <Trans>CSV</Trans>
          </Button>
        </div>
        <p className="w-full text-xs text-slate-500">
          {revision ? (
            <Trans>
              Depuis le jour de l'intervention jusqu'à la reprise du même problème ou de la même zone ;
              un patient non repris compte jusqu'à sa dernière date de suivi documentée (censure).
            </Trans>
          ) : (
            <Trans>
              Depuis l'ouverture de l'épisode jusqu'à l'intervention ; un patient non opéré compte jusqu'à sa
              dernière date documentée (censure), au lieu d'être oublié comme dans une moyenne des délais.
            </Trans>
          )}
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!result && loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {result && (
        <div className={cn("space-y-4 transition-opacity", loading && "opacity-60")}>
          <WarningsList warnings={result.warnings} />
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 flex flex-wrap items-center gap-4 text-xs text-slate-600">
              {result.groups.map((group, index) => (
                <span key={group.id} className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: groupColor(index) }} />
                  {group.label}
                </span>
              ))}
              <label className="ml-auto flex items-center gap-2 text-slate-500">
                <Switch
                  checked={bands ?? result.groups.length <= 2}
                  onCheckedChange={(checked) => setBands(checked)}
                />
                <Trans>Intervalles de confiance</Trans>
              </label>
              <span className="text-slate-400">
                {revision ? t`Proportion sans reprise` : t`Proportion déjà opérée`}
              </span>
            </div>
            <SurvivalChart
              groups={result.groups}
              curves={result.curves}
              event={result.event}
              grid={result.grid}
              showBands={bands ?? result.groups.length <= 2}
            />
            <RiskTable result={result} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">
                    <Trans>Groupe</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">n</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Événements</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-left font-medium">
                    <Trans>Médiane (mois)</Trans>
                  </th>
                  {result.horizons.map((months) => (
                    <th key={months} className="px-3 py-2.5 text-left font-medium">
                      {revision ? <Trans>Sans reprise à {months} mois</Trans> : <Trans>Opérés à {months} mois</Trans>}
                    </th>
                  ))}
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Suivi médian (mois)</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.groups.map((group, index) => {
                  const curve = result.curves[index];
                  return (
                    <tr key={group.id} className="border-t border-slate-200">
                      <td className="px-4 py-2.5">
                        <span className="flex items-center gap-1.5 font-medium text-slate-900">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: groupColor(index) }} />
                          {group.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{curve.n ?? "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{curve.events ?? "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {curve.median.value === null ? (
                          <span className="text-slate-400">
                            <Trans>non atteinte</Trans>
                          </span>
                        ) : (
                          <>
                            {formatNumber(curve.median.value, locale, 1)}{" "}
                            <span className="text-slate-400">
                              [{formatNumber(curve.median.low, locale, 1)} ;{" "}
                              {curve.median.high === null ? t`non atteinte` : formatNumber(curve.median.high, locale, 1)}]
                            </span>
                          </>
                        )}
                      </td>
                      {curve.at.map((point) => {
                        const value = point.survival === null ? null : revision ? point.survival : 1 - point.survival;
                        const low = point.low === null ? null : revision ? point.low : 1 - (point.high ?? 1);
                        const high = point.high === null ? null : revision ? point.high : 1 - (point.low ?? 0);
                        return (
                          <td key={point.months} className="px-3 py-2.5 tabular-nums">
                            {value === null ? (
                              "—"
                            ) : (
                              <>
                                {formatShare(value, locale)}{" "}
                                <span className="text-slate-400">
                                  [{formatShare(low, locale)} ; {formatShare(high, locale)}]
                                </span>
                              </>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {formatNumber(curve.follow_up_median, locale, 1)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {result.compares && result.test.interpretation && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800">
              <p>{result.test.interpretation}</p>
              {result.test.estimates[0] && (
                <p className="mt-1 text-xs text-slate-600">
                  {result.test.estimates[0].name} {formatNumber(result.test.estimates[0].value, locale, 2)}{" "}
                  {formatInterval(result.test.estimates[0].low, result.test.estimates[0].high, locale)}
                  <span className="text-slate-400">
                    {" "}
                    · χ² {formatNumber(result.test.statistic, locale, 2)} · p {formatP(result.test.p, locale)}
                  </span>
                </p>
              )}
            </div>
          )}
          <MethodsBox text={result.methods} />
        </div>
      )}
    </div>
  );
}

/** Patients still followed at each column of time — what keeps a late curve honest. */
function RiskTable({ result }: { result: SurvivalResult }) {
  if (result.grid.length === 0) return null;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-2xs text-slate-500">
        <thead>
          <tr>
            <th className="pr-3 text-left font-medium">
              <Trans>À risque</Trans>
            </th>
            {result.grid.map((month) => (
              <th key={month} className="px-1 text-right font-normal tabular-nums">
                {month}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.groups.map((group, index) => (
            <tr key={group.id}>
              <td className="max-w-[10rem] pr-3 text-slate-700">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: groupColor(index) }} />
                  <span className="truncate">{group.label}</span>
                </span>
              </td>
              {result.curves[index].risk_table.map((count, column) => (
                <td key={column} className="px-1 text-right tabular-nums text-slate-600">
                  {count ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
